export interface PlanLimits {
    max_seats: number;
    max_clients: number;
    max_brand_applications: number;
    white_label_level: 'none' | 'basic' | 'full';
    can_publish_videos: boolean;
}

export interface PlanUsage {
    seats: number;
    clients: number;
    brand_applications: number;
}

export interface PlanSummary {
    plan: {
        id: number;
        name: string;
        code: string;
        price: number;
        organization_type: string;
        white_label_level: string;
        can_publish_videos: boolean;
        support_level: string;
        chatbot_enabled: boolean;
    } | null;
    is_fallback: boolean;
    subscription: {
        id: number;
        status: string | null;
        payment_status: string;
        is_subscription: boolean;
        is_manual_assignment: boolean;
        start_date: string | null;
        end_date: string | null;
        next_invoice_date: string | null;
        plan_name: string;
    } | null;
    pending_subscription: { id: number; plan_name: string; flow_url: string | null } | null;
    limits: PlanLimits;
    usage: PlanUsage;
}

export interface OrganizationData {
    id: number;
    name: string;
    slug: string;
    organization_type: 'independent' | 'firm';
    organization_type_display: string;
    rut: string;
    email: string;
    phone: string;
    address: string;
    status: 'active' | 'suspended' | 'closed';
    status_display: string;
    suspension_reason: string | null;
    owner_id: number;
    owner_name: string | null;
    owner_email: string | null;
    created_at: string;
    // Contrato de encargo de tratamiento de datos
    dpa_accepted?: boolean;
    dpa_version?: string | null;
    dpa_accepted_at?: string | null;
    // Baja del estudio
    closed_at?: string | null;
    purge_after?: string | null;
}

export interface Member {
    id: number;
    user_id: number | null;
    email: string;
    name: string;
    // 'admin' | 'lawyer' | 'assistant' o 'custom:<id>' si tiene un rol propio del estudio
    role: string;
    role_display: string;
    // Rol base (define qué puede hacer por defecto): admin | lawyer | assistant
    base_role?: 'admin' | 'lawyer' | 'assistant';
    status: 'invited' | 'active' | 'removed';
    status_display: string;
    is_owner: boolean;
    invited_by: string | null;
    invitation_sent_at: string | null;
    invitation_expires_at: string | null;
    joined_at: string | null;
    avatar: string | null;
}

export interface LawyerPlan {
    id: number;
    name: string;
    code: string;
    organization_type: 'independent' | 'firm';
    description: string;
    short_description: string;
    price: number;
    price_with_tax: number;
    is_free: boolean;
    max_seats: number;
    max_clients: number;
    max_brand_applications: number;
    white_label_level: 'none' | 'basic' | 'full';
    can_publish_videos: boolean;
    chatbot_enabled: boolean;
    chatbot_messages_limit: number;
    support_level: string;
    support_level_display: string;
    features: string[];
    is_featured: boolean;
    badge_text: string;
}

export interface BuiltinRole {
    value: 'admin' | 'lawyer' | 'assistant';
    name: string;
    description: string;
}

export interface OrganizationRole {
    id: number;
    value: string; // custom:<id>
    name: string;
    description: string;
    capabilities: string[];
    can_be_assigned: boolean;
    members_count: number;
}

export interface CapabilityItem {
    key: string;
    group: string;
    label: string;
}

export const ROLE_OPTIONS = [
    { value: 'admin', label: 'Administrador del estudio' },
    { value: 'lawyer', label: 'Abogado' },
    { value: 'assistant', label: 'Asistente' }
];

export const WHITE_LABEL_LABELS: Record<string, string> = {
    none: 'Sin personalización',
    basic: 'Básico (logo, colores y nombre)',
    full: 'Completo (incluye correo y subdominio)'
};

export const formatCLP = (value: number) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(value || 0);

export const formatDate = (value?: string | null) =>
    value ? new Date(value).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

export const limitText = (value: number) => (value === -1 ? 'Ilimitados' : String(value));

/**
 * Solo se redirige a URLs https de Flow (pagos y registro de tarjeta). Evita que
 * una URL inesperada del backend (javascript:, otro dominio) termine abriéndose.
 */
export const isSafePaymentUrl = (url?: string | null): url is string => {
    if (!url) return false;
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:' && (parsed.hostname === 'flow.cl' || parsed.hostname.endsWith('.flow.cl'));
    } catch {
        return false;
    }
};

/**
 * Para redirigir a un pago: deja pasar rutas internas (/intranet/...) y URLs https
 * de Flow; cualquier otra cosa (otro dominio, javascript:) vuelve a la intranet.
 */
export const safeRedirectUrl = (url?: string | null): string => {
    if (url && url.startsWith('/') && !url.startsWith('//')) return url;
    return isSafePaymentUrl(url) ? url : '/intranet';
};

export const SUBSCRIPTION_STATUS_LABELS: Record<string, string> = {
    active: 'activa',
    trial: 'en período de prueba',
    inactive: 'inactiva',
    cancelled: 'cancelada',
    morose: 'con pago pendiente',
    pending: 'pendiente',
    paid: 'pagada',
    failed: 'fallida'
};

export const statusLabel = (status?: string | null) => (status ? SUBSCRIPTION_STATUS_LABELS[status] || status : '');

export const CONTRACT_STATUS_LABELS: Record<string, string> = {
    draft: 'Borrador',
    preview_generated: 'Preview generado',
    pending_payment: 'Pendiente de pago',
    pending: 'Pendiente',
    paid: 'Pagado',
    completed: 'Completado',
    cancelled: 'Cancelado',
    failed: 'Fallido',
    not_required: 'No requiere firma',
    pending_signatures: 'Pendiente de firmas',
    partially_signed: 'Parcialmente firmado',
    fully_signed: 'Firmado'
};

export const contractLabel = (status?: string | null) => (status ? CONTRACT_STATUS_LABELS[status] || status : '—');
